import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { DRAG_DROP_PASTE } from '@lexical/rich-text';
import type { PointType } from 'lexical';
import {
  $getNodeByKey,
  $getSelection,
  $insertNodes,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  COMMAND_PRIORITY_LOW,
} from 'lexical';
import { useEffect } from 'react';

import { $createImageNode } from './ImageNode';
import { useImageHandlers } from './useImageHandlers';

/**
 * Uploads images dropped or pasted into the editor, then inserts them where they landed.
 * - `RichTextPlugin` moves the caret to the drop point before dispatching `DRAG_DROP_PASTE`.
 * - Nodes are only inserted once uploaded, so `onChange` never sees a temporary `blob:` URL.
 */
export const ImageDropPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const { upload } = useImageHandlers();

  useEffect(() => {
    if (!upload) return;

    return editor.registerCommand(
      DRAG_DROP_PASTE,
      (files) => {
        const images = files.filter((file) => file.type.startsWith('image/'));
        if (images.length === 0) return false;

        // Remember the drop point; the user may keep typing while uploads run
        const selection = $getSelection();
        const dropSelection = $isRangeSelection(selection) ? selection.clone() : null;

        void Promise.allSettled(images.map((file) => upload(file))).then((results) => {
          const uploaded = results.flatMap((result) => {
            if (result.status === 'fulfilled') return [result.value];
            console.error('Image upload failed', result.reason);
            return [];
          });
          if (uploaded.length === 0) return;

          editor.update(() => {
            if (dropSelection && $isPointValid(dropSelection.anchor) && $isPointValid(dropSelection.focus)) $setSelection(dropSelection);
            $insertNodes(uploaded.map(({ url, fileId }) => $createImageNode({ src: url, fileId })));
          });
        });

        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, upload]);

  return null;
};

/** Edits during the upload can delete or shorten the node a saved point refers to. */
const $isPointValid = ({ key, offset }: PointType) => {
  const node = $getNodeByKey(key);
  if ($isTextNode(node)) return offset <= node.getTextContentSize();
  if ($isElementNode(node)) return offset <= node.getChildrenSize();
  return false;
};
